import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import {
  MessageSquare, Users, Target, BarChart3, FileText,
  ChevronRight, Sparkles, TrendingUp, Brain
} from 'lucide-react';

const tabs = [
  {
    id: 'reflect',
    label: 'Reflect',
    icon: MessageSquare,
    title: 'Your AI Companion',
    description: 'A conversation that remembers. It knows your team, your projects, your patterns — and asks the right questions.',
    features: [
      'Remembers every person and project you mention',
      'Connects today\'s challenge to last month\'s insight',
      'Suggests relevant book content at the right moment',
    ],
    mockup: (accent) => (
      <div className="space-y-3">
        <MockBubble align="right" color={accent}>
          Had a tough 1:1 with Sarah today. She pushed back hard on the new process.
        </MockBubble>
        <MockBubble align="left">
          That sounds like it took you off guard. Last time Sarah resisted a change, you found that asking her to co-design the solution worked well. What was different this time?
        </MockBubble>
        <MockBubble align="right" color={accent}>
          I think I presented it as final, not as something we could shape together.
        </MockBubble>
        <MockBubble align="left">
          That&apos;s a strong insight. You&apos;ve noticed the pattern — when you involve people early, adoption improves. How could you reopen this with Sarah?
        </MockBubble>
        <div className="flex items-center gap-2 pt-1">
          <div className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 rounded-full">
            <Brain className="w-3 h-3 text-purple-400" />
            <span className="text-[10px] text-purple-400 font-medium">Capability detected: Stakeholder Communication</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'people',
    label: 'People',
    icon: Users,
    title: 'People You\'re Developing',
    description: 'Every person you mention in reflections gets tracked. Build a picture of your relationships over time.',
    features: [
      'Auto-extracted from your conversations',
      'See themes and patterns per person',
      'Track how your approach evolves',
    ],
    mockup: () => (
      <div className="space-y-2.5">
        {[
          { name: 'Sarah.C', role: 'Senior Developer', mentions: 14, theme: 'Resistance & Trust', color: '#3DD3EE' },
          { name: 'Marcus.W', role: 'Team Lead', mentions: 9, theme: 'Delegation', color: '#B4DC19' },
          { name: 'Priya.S', role: 'New Hire', mentions: 7, theme: 'Onboarding & Coaching', color: '#FAD24B' },
          { name: 'James.O', role: 'Product Manager', mentions: 5, theme: 'Cross-team alignment', color: '#C8AFF0' },
        ].map((person) => (
          <div key={person.name} className="flex items-center gap-3 p-2.5 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                 style={{ backgroundColor: person.color + '30', color: person.color }}>
              {person.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white">{person.name}</span>
                <span className="text-[10px] text-slate-500">{person.role}</span>
              </div>
              <div className="text-xs text-slate-400">{person.theme}</div>
            </div>
            <div className="text-xs text-slate-500">{person.mentions} reflections</div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'growth',
    label: 'Growth',
    icon: TrendingUp,
    title: 'Patterns & Progress',
    description: 'Your reflections reveal patterns you can\'t see in the moment. Weekly and monthly summaries surface what matters.',
    features: [
      'Weekly narrative summaries of your leadership themes',
      'Recurring pattern detection across conversations',
      'Behavioural change tracking over time',
    ],
    mockup: (accent) => (
      <div className="space-y-3">
        <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: accent }} />
            <span className="text-xs font-semibold text-slate-300">Weekly Pattern — Mar 10-16</span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            This week you reflected 4 times, primarily on <span className="text-white font-medium">stakeholder communication</span> and <span className="text-white font-medium">delegation</span>. A recurring theme: you default to presenting decisions as final rather than inviting co-creation. When you do invite input, outcomes improve.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Reflections', value: '23', sub: 'this month' },
            { label: 'Top Domain', value: 'People', sub: 'development' },
            { label: 'Growth', value: '+3', sub: 'capabilities' },
          ].map((stat) => (
            <div key={stat.label} className="p-2.5 bg-slate-800/50 rounded-lg text-center">
              <div className="text-lg font-bold text-white">{stat.value}</div>
              <div className="text-[10px] text-slate-500">{stat.label}</div>
              <div className="text-[10px] text-slate-600">{stat.sub}</div>
            </div>
          ))}
        </div>
        <div className="p-3 bg-slate-800/50 rounded-lg border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-400">Behavioural Shift Detected</span>
          </div>
          <p className="text-xs text-slate-400">You&apos;ve moved from <em>telling</em> to <em>asking</em> in feedback conversations. This shift appeared in 3 of your last 5 reflections.</p>
        </div>
      </div>
    ),
  },
  {
    id: 'cv',
    label: 'Living CV',
    icon: FileText,
    title: 'Your Living CV',
    description: 'Every reflection builds evidence of your leadership capability. Not a list of courses — a record of demonstrated behaviour.',
    features: [
      'Structured against 6 leadership domains',
      'Real evidence from real situations',
      'Export-ready for reviews, promotions, interviews',
    ],
    mockup: (accent) => (
      <div className="space-y-2.5">
        {[
          { domain: 'Stakeholder Communication', level: 'Senior', evidence: 8, color: '#C8AFF0', width: '75%' },
          { domain: 'People Development', level: 'Mid', evidence: 6, color: '#3DD3EE', width: '55%' },
          { domain: 'Delivery & Execution', level: 'Senior', evidence: 5, color: '#B4DC19', width: '65%' },
          { domain: 'Personal Leadership', level: 'Mid', evidence: 4, color: '#FAD24B', width: '45%' },
          { domain: 'Operational Excellence', level: 'Junior', evidence: 2, color: '#FF8C19', width: '25%' },
          { domain: 'Strategic Leadership', level: 'Junior', evidence: 1, color: '#283750', width: '15%' },
        ].map((d) => (
          <div key={d.domain} className="p-2.5 bg-slate-800/50 rounded-lg">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-white">{d.domain}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500">{d.evidence} evidence</span>
                <Badge className="text-[9px] px-1.5 py-0 border-0" style={{ backgroundColor: d.color + '20', color: d.color }}>
                  {d.level}
                </Badge>
              </div>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: d.width, backgroundColor: d.color }} />
            </div>
          </div>
        ))}
      </div>
    ),
  },
];

const MockBubble = ({ children, align, color }) => (
  <div className={`flex ${align === 'right' ? 'justify-end' : 'justify-start'}`}>
    <div
      className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
        align === 'right'
          ? 'rounded-br-md text-white'
          : 'rounded-bl-md bg-slate-800 text-slate-300'
      }`}
      style={align === 'right' ? { backgroundColor: (color || '#6366f1') + 'cc' } : undefined}
    >
      {children}
    </div>
  </div>
);

const DashboardPreview = ({ series }) => {
  const [activeTab, setActiveTab] = useState('reflect');
  const accent = series?.accentColor || '#DC2626';
  const activeData = tabs.find(t => t.id === activeTab);

  return (
    <section className="py-20 bg-slate-950 relative overflow-hidden" id="dashboard-preview">
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: accent }}>
            Inside the Companion
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
            More than a chatbot. A <span style={{ color: accent }}>leadership system.</span>
          </h2>
          <p className="text-base text-slate-400 max-w-xl mx-auto">
            Reflect, track people, spot patterns, and build a Living CV of demonstrated capability — all from one conversation.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Tabs */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-slate-900 rounded-xl p-1 border border-slate-800">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-slate-800 text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-300'
                    }`}
                    data-testid={`preview-tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" style={isActive ? { color: accent } : undefined} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Left: description + features */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-xl font-bold text-white">{activeData.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{activeData.description}</p>
              <ul className="space-y-2">
                {activeData.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }} />
                    <span className="text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: mockup */}
            <div className="lg:col-span-3">
              <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5 shadow-2xl shadow-black/30">
                {/* Window chrome */}
                <div className="flex items-center gap-1.5 mb-4 pb-3 border-b border-slate-800">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  <span className="text-[10px] text-slate-600 ml-3">30 Day Skills — Dashboard</span>
                </div>
                {activeData.mockup(accent)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardPreview;
